import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { ClrCheckboxDisplayDirective } from 'src/app/directives/clrCheckboxDisplay.directive';
import { APIService } from 'src/app/services/APIService/api.service';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { CommonModule } from '@angular/common';
import { ClarityModule, ClrDatagrid } from '@clr/angular';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { rxResource } from '@angular/core/rxjs-interop';
import { Table } from 'src/app/models/ui/table';
import { User } from 'src/app/models/api/User';
import { AlertUI, Alert, RepeatOptions, BerichtID } from './berichte.types.ts.js';
import { BERICHTE_CONFIG } from './berichte-config.token.js';
import { AlertsStateLoader, AlertsStore } from './berichte.store.js';

@Component({
  selector: 'app-berichte-config-modal',
  templateUrl: './berichte-config-modal.component.html',
  styleUrls: ['./berichte-config-modal.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    ClrCheckboxDisplayDirective,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [AlertsStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BerichteConfigModalComponent {
  public readonly isOpen = model.required<boolean>();
  private readonly userDatagrid = viewChild<ClrDatagrid<User>>('userDg');

  protected readonly store = inject(AlertsStore);
  protected readonly config = inject(BERICHTE_CONFIG);
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);

  protected readonly usersResource = rxResource({
    params: this.isOpen,
    stream: () =>
      this.apiService.getUsers().pipe(
        map((table: Table<User>) => {
          table.rows = table.rows.filter(user => !!user.UserMail);
          return table;
        })
      ),
  });

  private readonly alertsResource = rxResource<Alert[], boolean>({
    params: this.isOpen,
    stream: () => this.apiService.getBerichte(),
  });

  readonly #stateLoader = computed<AlertsStateLoader>(() => ({
    alerts: this.alertsResource.value(),
    users: this.usersResource.value()?.rows,
  }));

  protected readonly berichte = this.store.berichte;

  protected readonly selectedAlert = signal<AlertUI>(null);
  protected readonly selectedUsers = signal<User[]>([]);

  protected readonly saved = signal(true);
  protected readonly description = signal<string>(null);
  #deletedAlerts: number[] = [];

  constructor() {
    this.store.load(this.#stateLoader);
    this.store.reload(computed(() => this.alertsResource.status() == 'reloading'));
    effect(() => {
      const selected = this.selectedAlert();
      if (!selected) return;

      untracked(() => {
        const userIDs = selected.users.idArr;
        const users = this.usersResource.value().rows;
        this.userDatagrid().selected = users.filter(user => userIDs.includes(user.id));
      });
    });
  }

  protected updateExpandedInBerichGroup(expanded: boolean, berichtID: BerichtID) {
    const group = this.store.berichte()[berichtID];
    group.expanded = expanded;
    this.store.updateBerichtsGroup(berichtID);
  }

  protected updateUsersInAlert() {
    this.store.updateUsersInAlert(this.selectedAlert(), this.selectedUsers());
    this.saved.set(false);
  }

  protected updateTerminInAlert(termin: RepeatOptions) {
    this.store.updateTerminInAlert(this.selectedAlert(), termin);
    this.saved.set(false);
  }

  protected updateFormatInAlert(format: 0 | 1 /* '*.xlsx' | '*.pdf' */) {
    this.store.updateFormatInAlert(this.selectedAlert(), format);
    this.saved.set(false);
  }

  protected newAlert(berichtID: BerichtID) {
    const neuAlert = this.store.addNewAlert(berichtID);
    this.selectedAlert.set(neuAlert);
    this.saved.set(false);
  }

  protected deleteAlert(berichtID: BerichtID, alert: AlertUI) {
    this.store.deleteAlert(berichtID, alert);
    this.selectedAlert.set(undefined);
    this.description.set(null);
    this.saved.set(false);
    if (alert.ID) this.#deletedAlerts.push(alert.ID);
  }

  protected toggleActive(isOn: boolean, berichtID: BerichtID, alert: AlertUI) {
    this.store.toggleActive(isOn, berichtID, alert);
    this.saved.set(false);
  }

  protected async saveBerichteConfig() {
    const sendObj = {
      UpdatedBerichte: this.store.getChangedAlerts(),
      DeletedBerichte: this.#deletedAlerts,
    };
    const { success, reload, error } = await firstValueFrom(this.apiService.setBerichte(sendObj));
    if (success) {
      this.toastr.success(this.mrTranslate.transform('Einstellungen erfolgreich gespeichert.'));
      this.saved.set(true);
      if (reload) this.alertsResource.reload();
      else this.store.resetIsChangedInAlerts();
      this.#deletedAlerts = [];
    } else this.toastr.error(error, this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
  }
}
